'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';
import { motion } from 'framer-motion';

import { cn } from '@/lib/utils';

interface TabsContextValue {
	activeValue?: string;
	direction: -1 | 0 | 1;
	animationTick: number;
	enableContentAnimation: boolean;
	registerTrigger: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

interface TabsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
	enableContentAnimation?: boolean;
}

const getDirection = (
	fromValue: string | undefined,
	toValue: string | undefined,
	tabOrder: string[]
): -1 | 0 | 1 => {
	if (!fromValue || !toValue || fromValue === toValue) {
		return 0;
	}

	const fromIndex = tabOrder.indexOf(fromValue);
	const toIndex = tabOrder.indexOf(toValue);

	if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
		return 0;
	}

	return toIndex > fromIndex ? 1 : -1;
};

function Tabs({
	className,
	value,
	defaultValue,
	onValueChange,
	enableContentAnimation = true,
	...props
}: TabsProps) {
	const controlledValue = typeof value === 'string' ? value : undefined;
	const [uncontrolledValue, setUncontrolledValue] = React.useState<
		string | undefined
	>(
		typeof defaultValue === 'string'
			? defaultValue
			: controlledValue
	);
	const activeValue = controlledValue ?? uncontrolledValue;
	const previousValueRef = React.useRef<string | undefined>(activeValue);
	const [direction, setDirection] = React.useState<-1 | 0 | 1>(0);
	const [animationTick, setAnimationTick] = React.useState(0);
	const [tabOrder, setTabOrder] = React.useState<string[]>([]);

	const registerTrigger = React.useCallback((triggerValue: string) => {
		setTabOrder(previous =>
			previous.includes(triggerValue)
				? previous
				: [...previous, triggerValue]
		);
	}, []);

	const handleValueChange = React.useCallback(
		(nextValue: string) => {
			const currentValue = controlledValue ?? previousValueRef.current;
			const nextDirection = getDirection(currentValue, nextValue, tabOrder);
			setDirection(nextDirection);
			if (nextDirection !== 0) {
				setAnimationTick(previous => previous + 1);
			}

			previousValueRef.current = nextValue;

			if (controlledValue === undefined) {
				setUncontrolledValue(nextValue);
			}

			onValueChange?.(nextValue);
		},
		[controlledValue, onValueChange, tabOrder]
	);

	React.useEffect(() => {
		if (controlledValue === undefined) {
			return;
		}

		const previousValue = previousValueRef.current;
		const nextDirection = getDirection(
			previousValue,
			controlledValue,
			tabOrder
		);
		setDirection(nextDirection);
		if (nextDirection !== 0) {
			setAnimationTick(previous => previous + 1);
		}
		previousValueRef.current = controlledValue;
	}, [controlledValue, tabOrder]);

	const contextValue = React.useMemo<TabsContextValue>(
		() => ({
			activeValue,
			direction,
			animationTick,
			enableContentAnimation,
			registerTrigger
		}),
		[
			activeValue,
			direction,
			animationTick,
			enableContentAnimation,
			registerTrigger
		]
	);

	return (
		<TabsContext.Provider value={contextValue}>
			<TabsPrimitive.Root
				data-slot="tabs"
				className={cn('flex flex-col', className)}
				value={value}
				defaultValue={defaultValue}
				onValueChange={handleValueChange}
				{...props}
			/>
		</TabsContext.Provider>
	);
}

function TabsList({
	className,
	...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
	return (
		<TabsPrimitive.List
			data-slot="tabs-list"
			className={cn(
				'flex items-center gap-4 bg-muted/30 rounded-none px-4 py-3 overflow-hidden',
				'w-[calc(100%+2rem)] -ml-4',
				className
			)}
			{...props}
		/>
	);
}

interface TabsTriggerProps
	extends React.ComponentProps<typeof TabsPrimitive.Trigger> {
	align?: 'left' | 'right';
}

function TabsTrigger({
	className,
	align = 'left',
	...props
}: TabsTriggerProps) {
	const tabsContext = React.useContext(TabsContext);
	const triggerValue = props.value;
	const triggerRef = React.useRef<HTMLButtonElement | null>(null);
	const [hideIcon, setHideIcon] = React.useState(false);

	const updateIconVisibility = React.useCallback(() => {
		const triggerElement = triggerRef.current;
		if (!triggerElement) {
			return;
		}

		const hasDirectIcon = Array.from(triggerElement.children).some(
			child => child.tagName.toLowerCase() === 'svg'
		);

		if (!hasDirectIcon) {
			setHideIcon(false);
			return;
		}

		// Measure overflow with icon visible; hide icon only when content does not fit.
		const previousHideIconAttr = triggerElement.getAttribute('data-hide-icon');
		triggerElement.setAttribute('data-hide-icon', 'false');
		const isOverflowing = triggerElement.scrollWidth > triggerElement.clientWidth + 1;

		if (previousHideIconAttr == null) {
			triggerElement.removeAttribute('data-hide-icon');
		} else {
			triggerElement.setAttribute('data-hide-icon', previousHideIconAttr);
		}

		setHideIcon(isOverflowing);
	}, []);

	React.useEffect(() => {
		if (typeof triggerValue !== 'string' || !tabsContext) {
			return;
		}

		tabsContext.registerTrigger(triggerValue);
	}, [tabsContext, triggerValue]);

	React.useLayoutEffect(() => {
		const triggerElement = triggerRef.current;
		if (!triggerElement) {
			return;
		}

		let rafId: number | null = null;
		const scheduleMeasurement = () => {
			if (rafId != null) {
				cancelAnimationFrame(rafId);
			}

			rafId = requestAnimationFrame(() => {
				updateIconVisibility();
				rafId = null;
			});
		};

		scheduleMeasurement();

		const resizeObserver =
			typeof ResizeObserver !== 'undefined'
				? new ResizeObserver(scheduleMeasurement)
				: null;
		resizeObserver?.observe(triggerElement);

		return () => {
			if (rafId != null) {
				cancelAnimationFrame(rafId);
			}
			resizeObserver?.disconnect();
		};
	}, [updateIconVisibility, props.children, className, triggerValue]);

	return (
		<TabsPrimitive.Trigger
			data-slot="tabs-trigger"
			ref={triggerRef}
			data-hide-icon={hideIcon ? 'true' : 'false'}
			className={cn(
				"relative inline-flex min-w-0 shrink items-center gap-2 overflow-hidden text-ellipsis rounded-none border-none bg-transparent px-0 py-0 mr-3 text-[0.9rem] font-normal text-muted-foreground shadow-none transition-colors whitespace-nowrap",
				// Hover: subtle color change like a link
				"hover:text-primary",
				// Active: bold primary text like a selected link
				"data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:font-semibold data-[state=active]:shadow-none data-[state=active]:px-0 data-[state=active]:py-0",
				// Active hover: stay primary
				"data-[state=active]:hover:text-primary",
				"data-[hide-icon=true]:[&>svg]:hidden",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				"disabled:pointer-events-none disabled:opacity-50",
				"[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
				"[&>span]:min-w-0 [&>span]:whitespace-nowrap",
				align === 'right' && 'ml-auto',
				className
			)}
			{...props}
		/>
	);
}

function TabsContent({
	className,
	children,
	...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
	const tabsContext = React.useContext(TabsContext);
	const contentValue = props.value;

	React.useEffect(() => {
		if (typeof contentValue !== 'string' || !tabsContext) {
			return;
		}

		tabsContext.registerTrigger(contentValue);
	}, [tabsContext, contentValue]);

	const isActive =
		typeof contentValue === 'string' &&
		contentValue === tabsContext?.activeValue;
	const shouldAnimate =
		Boolean(isActive) &&
		Boolean(tabsContext?.enableContentAnimation) &&
		tabsContext?.direction !== 0;
	const contentKey = isActive
		? `${contentValue}-${tabsContext?.animationTick ?? 0}`
		: String(contentValue ?? '');

	return (
		<TabsPrimitive.Content
			data-slot="tabs-content"
			className={cn('flex-1 outline-none pt-4', className)}
			{...props}
		>
			<motion.div
				key={contentKey}
				initial={
					shouldAnimate
						? { x: (tabsContext?.direction ?? 0) > 0 ? 24 : -24 }
						: { x: 0 }
				}
				animate={{ x: 0 }}
				transition={{ duration: 0.16, ease: 'easeOut' }}
			>
				{children}
			</motion.div>
		</TabsPrimitive.Content>
	);
}

interface TabLinkProps {
	value: string;
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	align?: 'left' | 'right';
}

/**
 * Pre-styled tab trigger that renders as a link with icon + label.
 * Uses an inline-grid trick to reserve bold width and prevent layout shift.
 */
function TabLink({ value, icon: Icon, label, align }: TabLinkProps) {
	return (
		<TabsTrigger value={value} align={align}>
			<Icon className="w-4 h-4" />
			{/*
			 * Width-reservation trick: both spans occupy the same grid cell (row 1, col 1).
			 * The visible span shows the actual label. The hidden span renders the same text
			 * in bold (font-semibold) so the cell always reserves the wider bold width.
			 * This prevents neighbouring tabs from shifting when the active tab becomes bold.
			 */}
			<span className="inline-grid min-w-0">
				<span className="col-start-1 row-start-1 truncate">{label}</span>
				<span className="col-start-1 row-start-1 font-semibold invisible pointer-events-none truncate" aria-hidden="true">{label}</span>
			</span>
		</TabsTrigger>
	);
}

export { Tabs, TabsContent, TabsList, TabsTrigger, TabLink };
